import { useState } from 'react';

interface TimeFormatters {
  t: (key: string) => string;
  formatTime: (date: Date) => string;
  formatShortDate: (date: Date) => string;
}
import { Search, Plus, MoreVertical, Phone, Video, Send, Paperclip, Smile } from 'lucide-react';
import { cn } from '../../../utils/helpers';
import { useTranslation } from '../../../hooks/useTranslation';
import { useLocale } from '../../../hooks/useLocale';

const conversations = [
  { id: 1, name: 'Sarah Wilson', lastMessage: 'The payment was successful!', time: (l: TimeFormatters) => l.formatTime(new Date(new Date().setHours(10, 24))), unread: 2, avatar: 'https://picsum.photos/seed/sarah/100/100', online: true },
  { id: 2, name: 'Design Team', lastMessage: 'New product designs are ready', time: (l: TimeFormatters) => l.t('common.yesterday'), unread: 0, avatar: 'https://picsum.photos/seed/team/100/100', online: false },
  { id: 3, name: 'Mike Ross', lastMessage: 'Can you review my application?', time: (l: TimeFormatters) => l.t('common.yesterday'), unread: 0, avatar: 'https://picsum.photos/seed/mike/100/100', online: true },
  { id: 4, name: 'Emma Watson', lastMessage: 'See you at the networking event', time: (l: TimeFormatters) => l.formatShortDate(new Date(2023, 9, 22)), unread: 0, avatar: 'https://picsum.photos/seed/emma/100/100', online: false },
];

export default function Messaging() {
  const { t } = useTranslation();
  const { formatTime, formatShortDate } = useLocale();
  const [activeChat, setActiveChat] = useState(conversations[0]);

  return (
    <div className="h-[calc(100vh-12rem)] lg:h-[calc(100vh-8rem)] flex bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
      {/* Sidebar */}
      <div className="w-full md:w-80 flex flex-col border-r border-neutral-100 dark:border-neutral-800">
        <div className="p-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
          <h2 className="text-xl font-bold">{t('messaging.title')}</h2>
          <button className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800">
            <Plus className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
          </button>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
            <input 
              type="text" 
              placeholder={t('messaging.searchChats')} 
              className="w-full pl-10 pr-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/5 transition-all"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.map((chat) => (
            <button
              key={chat.id}
              onClick={() => setActiveChat(chat)}
              className={cn(
                "w-full flex items-center gap-3 p-4 transition-colors border-b border-neutral-50 last:border-0",
                activeChat.id === chat.id ? "bg-neutral-50 dark:bg-neutral-950" : "hover:bg-neutral-50/50"
              )}
            >
              <div className="relative">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-800">
                  <img src={chat.avatar} alt={chat.name} referrerPolicy="no-referrer" />
                </div>
                {chat.online && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex justify-between items-center mb-0.5">
                  <p className="font-semibold text-sm truncate">{chat.name}</p>
                  <span className="text-[10px] text-neutral-400 dark:text-neutral-500">{chat.time({ t, formatTime, formatShortDate })}</span>
                </div>
                <p className={cn(
                  "text-xs truncate",
                  chat.unread > 0 ? "text-neutral-900 dark:text-neutral-50 font-semibold" : "text-neutral-500 dark:text-neutral-400"
                )}>
                  {chat.lastMessage}
                </p>
              </div>
              {chat.unread > 0 && (
                <div className="w-5 h-5 bg-neutral-900 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {chat.unread}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="hidden md:flex flex-1 flex-col bg-neutral-50/30">
        {/* Chat Header */}
        <div className="h-16 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-800">
              <img src={activeChat.avatar} alt={activeChat.name} referrerPolicy="no-referrer" />
            </div>
            <div>
              <p className="font-bold text-sm">{activeChat.name}</p>
              <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-wider">
                {activeChat.online ? t('common.online') : t('common.offline')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"><Phone className="w-5 h-5" /></button>
            <button className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"><Video className="w-5 h-5" /></button>
            <button className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"><MoreVertical className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex justify-center">
            <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-800 px-3 py-1 rounded-full uppercase tracking-widest">{t('common.today')}</span>
          </div>
          
          <div className="flex gap-3 max-w-[80%]">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-800 mt-auto">
              <img src={activeChat.avatar} alt={activeChat.name} referrerPolicy="no-referrer" />
            </div>
            <div className="bg-white dark:bg-neutral-900 p-4 rounded-2xl rounded-bl-none border border-neutral-100 dark:border-neutral-800 shadow-sm dark:shadow-none">
              <p className="text-sm">Hey! I just sent the payment for the new product designs. Can you confirm if you received it?</p>
              <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-2 text-right">{formatTime(new Date(new Date().setHours(10, 22)))}</p>
            </div>
          </div>

          <div className="flex gap-3 max-w-[80%] ml-auto">
            <div className="bg-neutral-900 text-white p-4 rounded-2xl rounded-br-none shadow-lg shadow-neutral-900/10">
              <p className="text-sm">The payment was successful! I'm starting the final export now. Will send them over in a bit.</p>
              <p className="text-[10px] text-white/50 mt-2 text-right">{formatTime(new Date(new Date().setHours(10, 24)))}</p>
            </div>
          </div>
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white dark:bg-neutral-900 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-2xl p-2">
            <button className="p-2 rounded-xl hover:bg-white dark:hover:bg-neutral-900 transition-colors text-neutral-500 dark:text-neutral-400"><Paperclip className="w-5 h-5" /></button>
            <input 
              type="text" 
              placeholder={t('messaging.typeMessage')} 
              className="flex-1 bg-transparent border-none focus:ring-0 text-sm px-2"
            />
            <button className="p-2 rounded-xl hover:bg-white dark:hover:bg-neutral-900 transition-colors text-neutral-500 dark:text-neutral-400"><Smile className="w-5 h-5" /></button>
            <button className="p-2 bg-neutral-900 text-white rounded-xl hover:bg-neutral-800 transition-colors">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
